import { computed, readonly, ref } from 'vue';
import { vitrineCardApi, type Capabilities } from '@/api/vitrineCard';
import { paymentsTabRoute } from '@/views/vitrine/concierge/conciergeUi';

/**
 * One shared copy of `/card/capabilities` for the vitrine (CONCIERGE_PAYMENTS_MODE.md §11.1): the card screen, the
 * top-up screen and the tab bar read the same answer from one load instead of each keeping a local ref.
 *
 * Fail-closed: until the server answered — and after any error — every flag reads `false`, exactly as the local refs
 * did before. The flags only drive the UI; the server checks every gate itself.
 */
const caps = ref<Capabilities | null>(null);
let inflight: Promise<Capabilities | null> | null = null;

function load(force = false): Promise<Capabilities | null> {
  if (!force && caps.value) return Promise.resolve(caps.value);
  if (!force && inflight) return inflight;
  const request = vitrineCardApi.capabilities()
    .then(c => {
      if (inflight === request) caps.value = c;
      return c;
    })
    .catch(() => {
      if (inflight === request) caps.value = null;   // fail-closed
      return null;
    })
    .finally(() => {
      if (inflight === request) inflight = null;
    });
  inflight = request;
  return request;
}

/** Forget the cached answer (sign-out: the next client on this device must not see the previous one's flags). */
function reset(): void {
  caps.value = null;
  inflight = null;
}

const newPayments = computed(() => caps.value?.newPayments ?? false);
const conciergePayments = computed(() => caps.value?.conciergePayments ?? false);
const conciergeHistory = computed(() => caps.value?.conciergeHistory ?? false);
/** The concierge pay form offers CRYPTO (USDT on TRON, §6.4); false before the answer. */
const conciergeCrypto = computed(() => caps.value?.conciergeCrypto ?? false);
/** The master account «Общий баланс» is on (docs/cabinet-redesign/04 §7, variant B); false before the answer. */
const masterAccount = computed(() => caps.value?.masterAccount ?? false);
/** The payments tab leads to the concierge screen (§11.1): the mode is on, or the client already has requests. */
const paymentsRoute = computed(() => paymentsTabRoute(caps.value));

export function useCapabilities() {
  return {
    capabilities: readonly(caps),
    newPayments,
    conciergePayments,
    conciergeHistory,
    conciergeCrypto,
    masterAccount,
    paymentsRoute,
    load,
    reset,
  };
}
